// tiles.js
// Handles tiled image loading, caching, layers, and safe destruction scheduling.

export class TileManager {
  constructor({ app, world, BASE_W, BASE_H, TILE_SIZE, URL_TEMPLATE, MAX_Z, sourceId = 0, sharedTexCache = null }) {
    this.app = app;
    this.world = world;
    this.BASE_W = BASE_W;
    this.BASE_H = BASE_H;
    this.TILE_SIZE = TILE_SIZE;
    this.URL_TEMPLATE = URL_TEMPLATE;
    this.MAX_Z = MAX_Z;

    // Scale at which the full image just fits the viewport (used to normalize zoom-layer selection)
    this.FIT_SCALE = 1;

    // "source" (aka {s} in URL template). For cube faces, this is the face id.
    this.sourceId = sourceId;

    this.TILE_BLEED_PX = 1; // 0.5..1 is typical


    this.FALLBACK_LAYERS = 2; // keep z, z-1, z-2 visible
    this.PAD_TILES = 1;
    this.TILE_CACHE_LIMIT = 450;

    // Zoom hysteresis
    this.Z_HYST_UP = 0.15;
    this.Z_HYST_DOWN = 0.15;

    this.currentZ = 0;

    this.tileLayers = new Map(); // z -> Container
    this.sprites = new Map();    // key -> { sp, z, loaded, mask? }

    // LRU texture cache (optionally shared across multiple TileManagers)
    // sharedTexCache shape: { texCache: Map, clock: { value:number } }
    this.texCache = sharedTexCache?.texCache || new Map(); // url -> { tex, last }
    this.sharedClock = sharedTexCache?.clock || null;
    this.clock = this.sharedClock ? (this.sharedClock.value || 0) : 0;

    // ==== SAFETY PATCH: defer redraw & sprite destruction ====
    this.tilesDirty = false;
    this.destroyQueue = [];
    this.destroyQueued = false;
  }

  getTileLayer(z) {
    if (!this.tileLayers.has(z)) {
      const c = new PIXI.Container();
      c.zIndex = z;
      this.world.addChild(c);
      this.tileLayers.set(z, c);
    }
    return this.tileLayers.get(z);
  }

  scheduleRedraw(fn) {
    if (this.tilesDirty) return;
    this.tilesDirty = true;
    this.app.ticker.addOnce(() => {
      this.tilesDirty = false;
      fn();
    });
  }

  queueDestroy(obj) {
    if (!obj) return;
    this.destroyQueue.push(obj);
    if (this.destroyQueued) return;
    this.destroyQueued = true;
    this.app.ticker.addOnce(() => {
      this.destroyQueued = false;
      while (this.destroyQueue.length) {
        const d = this.destroyQueue.pop();
        try { d.destroy(); } catch(e) {}
      }
    });
  }

  urlFor(z, s, x, y) {
    const ss = (s ?? this.sourceId);
    return this.URL_TEMPLATE
      .replace("{z}", z).replace("{s}", ss).replace("{x}", x).replace("{y}", y)
      .replace("{{z}}", z).replace("{{s}}", ss).replace("{{x}}", x).replace("{{y}}", y);
  }

  enforceCacheLimit() {
    if (this.texCache.size <= this.TILE_CACHE_LIMIT) return;
    const nEvict = Math.ceil(this.TILE_CACHE_LIMIT * 0.10);
    const entries = Array.from(this.texCache.entries()).sort((a,b) => a[1].last - b[1].last);
    for (let i=0; i<Math.min(nEvict, entries.length); i++) {
      const [url] = entries[i];
      this.texCache.delete(url);
    }
  }

  getTexture(url) {
    if (this.sharedClock) {
      this.sharedClock.value = (this.sharedClock.value || 0) + 1;
      this.clock = this.sharedClock.value;
    } else {
      this.clock++;
    }
    let e = this.texCache.get(url);
    if (!e) {
      // If tiles are hosted on a different origin, WebGL will render them as
      // black unless the server sends proper CORS headers and we request them
      // with crossorigin enabled.
      // (Safe even for same-origin.)
      const tex = PIXI.Texture.from(url, {
        resourceOptions: { crossorigin: "anonymous" },
      });
  
      // --- seam reduction ---
      const bt = tex.baseTexture;
      bt.wrapMode = PIXI.WRAP_MODES.CLAMP;                // avoid sampling from "outside"
      bt.mipmap = PIXI.MIPMAP_MODES.ON;                  // mipmaps can create seams
      // bt.scaleMode = PIXI.SCALE_MODES.LINEAR;          // default; keep smooth
      // bt.scaleMode = PIXI.SCALE_MODES.NEAREST;         // if you want pixel-crisp
  
      e = { tex, last: this.clock };
      this.texCache.set(url, e);
      this.enforceCacheLimit();
    } else {
      e.last = this.clock;
    }
    return e.tex;
  }
  

  pickZ(scale) {
    const norm = Math.max(1e-9, this.FIT_SCALE || 1);
    const s = Math.max(1e-9, scale) / norm;
    // Clamp very close to limits to avoid LOD thrash due to floating point noise
    let l = Math.log2(s);
    if (l >= this.MAX_Z - 1e-6) {
      this.currentZ = this.MAX_Z;
      return this.currentZ;
    }
    if (l <= 0 + 1e-6) {
      this.currentZ = 0;
      return this.currentZ;
    }
    const zFloor = Math.floor(l);
    const frac = l - zFloor;

    if (this.currentZ == null) this.currentZ = Math.max(0, Math.min(this.MAX_Z, zFloor));
    this.currentZ = Math.max(0, Math.min(this.MAX_Z, this.currentZ));

    if (this.currentZ === zFloor) {
      if (frac > 0.5 + this.Z_HYST_UP) this.currentZ = Math.min(this.MAX_Z, zFloor + 1);
      return this.currentZ;
    }

    if (this.currentZ === zFloor + 1) {
      if (frac < 0.5 - this.Z_HYST_DOWN) this.currentZ = Math.max(0, zFloor);
      return this.currentZ;
    }

    this.currentZ = Math.max(0, Math.min(this.MAX_Z, Math.round(l)));
    return this.currentZ;
  }

  // --- NEW: compute how much of the tile is inside the image at this zoom level ---
  tileClip(z, x, y) {
    const mul = Math.pow(2, z);

    // image size in "tile pixel space" at this z
    const Wz = this.BASE_W * mul;
    const Hz = this.BASE_H * mul;

    // tile rect in that same space
    const left   = x * this.TILE_SIZE;
    const top    = y * this.TILE_SIZE;
    const right  = left + this.TILE_SIZE;
    const bottom = top + this.TILE_SIZE;

    // intersection width/height in tile pixels
    const iw = Math.max(0, Math.min(right, Wz) - Math.max(left, 0));
    const ih = Math.max(0, Math.min(bottom, Hz) - Math.max(top, 0));

    return { iw, ih }; // 0..TILE_SIZE
  }

  // --- NEW: apply / update a rectangular mask so outside area is transparent ---
  applyTileMask(entry, z, x, y) {
    const { sp } = entry;
    const inv = 1 / Math.pow(2, z);
    const { iw, ih } = this.tileClip(z, x, y);
  
    const bleed = (this.TILE_BLEED_PX || 0) * inv;
  
    // full tile => no mask
    if (iw >= this.TILE_SIZE && ih >= this.TILE_SIZE) {
      if (entry.mask) {
        try { sp.mask = null; } catch(e) {}
        this.queueDestroy(entry.mask);
        entry.mask = null;
      }
      return;
    }
  
    if (!entry.mask) {
      entry.mask = new PIXI.Graphics();
      this.getTileLayer(z).addChild(entry.mask);
      sp.mask = entry.mask;
    }
  
    // Mask size = clipped size (+ bleed), but never exceed the sprite’s bounds
    const mw = Math.min(sp.width,  iw * inv + 2 * bleed);
    const mh = Math.min(sp.height, ih * inv + 2 * bleed);
  
    entry.mask.clear();
    entry.mask.beginFill(0xffffff, 1);
    entry.mask.drawRect(sp.x, sp.y, mw, mh);
    entry.mask.endFill();
  }
  

  ensureTile(z, x, y, s = this.sourceId) {
    const key = `${z}/${s}/${x}/${y}`;
    if (this.sprites.has(key)) return;

    const inv = 1 / Math.pow(2, z);
    const bleed = (this.TILE_BLEED_PX * inv);
    const sp = new PIXI.Sprite(PIXI.Texture.EMPTY);
    
    sp.x = x * this.TILE_SIZE * inv - bleed;
    sp.y = y * this.TILE_SIZE * inv - bleed;
    sp.width  = this.TILE_SIZE * inv + 2 * bleed;
    sp.height = this.TILE_SIZE * inv + 2 * bleed;
    
    this.getTileLayer(z).addChild(sp);

    const tex = this.getTexture(this.urlFor(z, s, x, y));

    // Assign the texture immediately.
    // PIXI will start rendering it as soon as the underlying BaseTexture becomes valid.
    // This avoids relying on specific load-event names across PIXI versions.
    sp.texture = tex;
    
    const entry = { sp, z, loaded: tex.baseTexture.valid, mask: null };
    this.sprites.set(key, entry);

    // NEW: mask edge tiles so missing area becomes transparent
    this.applyTileMask(entry, z, x, y);

    if (!tex.baseTexture.valid) {
      // Keep 'loaded' stats accurate. Rendering is handled by PIXI once valid.
      tex.baseTexture.once("loaded", () => {
        if (sp.destroyed || !this.sprites.has(key)) return;
        entry.loaded = true;
      });
      tex.baseTexture.once("error", () => {
        if (sp.destroyed || !this.sprites.has(key)) return;
      });
    }
  }

  pruneTiles(needed) {
    for (const [k, o] of this.sprites) {
      if (!needed.has(k)) {
        this.getTileLayer(o.z).removeChild(o.sp);
        if (o.mask) {
          this.getTileLayer(o.z).removeChild(o.mask);
          this.queueDestroy(o.mask);
          o.mask = null;
        }
        this.queueDestroy(o.sp);
        this.sprites.delete(k);
      }
    }
  }

  visibleBaseRect(view) {
    const inv = 1 / view.scale;
    return {
      l: (0 - view.tx) * inv,
      t: (0 - view.ty) * inv,
      r: (this.app.renderer.width - view.tx) * inv,
      b: (this.app.renderer.height - view.ty) * inv,
    };
  }

  // request tiles for current zoom and visible region.
  // visOverride (optional) is in BASE-image coordinates: {l,t,r,b}
  // Useful for non-rectangular / transformed views (e.g. skybox faces) where
  // only a portion of the face is actually on screen.
  requestTiles(view, visOverride = null) {
    const z = this.pickZ(view.scale);
    const vis = visOverride || this.visibleBaseRect(view);
    const needed = new Set();

    for (let zl = Math.max(0, z - this.FALLBACK_LAYERS); zl <= z; zl++) {
      const mul = Math.pow(2, zl);

      const x0 = Math.floor((vis.l * mul) / this.TILE_SIZE) - this.PAD_TILES;
      const y0 = Math.floor((vis.t * mul) / this.TILE_SIZE) - this.PAD_TILES;
      const x1 = Math.floor((vis.r * mul) / this.TILE_SIZE) + this.PAD_TILES;
      const y1 = Math.floor((vis.b * mul) / this.TILE_SIZE) + this.PAD_TILES;

      const maxX = Math.ceil((this.BASE_W * mul) / this.TILE_SIZE) - 1;
      const maxY = Math.ceil((this.BASE_H * mul) / this.TILE_SIZE) - 1;

      for (let yy = y0; yy <= y1; yy++) {
        for (let xx = x0; xx <= x1; xx++) {
          if (xx < 0 || yy < 0 || xx > maxX || yy > maxY) continue;
          this.ensureTile(zl, xx, yy, this.sourceId);
          needed.add(`${zl}/${this.sourceId}/${xx}/${yy}`);
        }
      }
    }

    this.pruneTiles(needed);

    return { z, spritesSize: this.sprites.size, loaded: Array.from(this.sprites.values()).filter(s => s.loaded).length };
  }
}
